import { useEffect, useMemo, useRef, useState } from 'react';
import { ScrollView, type LayoutChangeEvent } from 'react-native';
import {
  Flex,
  Typography,
  useScreenSize,
} from '@banhala/ui-kit';
import { isNil, localizeNumber } from 'shared/src/utils';
import type { OptionComponent } from 'shared/src/api/goodsService/getGoodsOptions/getGoodsOptions';
import { IconBrandIconThirdPL } from '../../../IconBrandIconThirdPL';
import { InlineTodayDeliveryIcon } from './InlineTodayDeliveryIcon';

const CHIP_MIN_WIDTH = 84;
const CHIP_MAX_WIDTH = 128;
const CHIP_BASE_HEIGHT = 64;
const CHIP_LINE_HEIGHT = 18;
const CHIP_STOCK_BLOCK_HEIGHT = 28;
const CHIP_PROPERTY_HEIGHT = 18;

type InlineOptionGroupView = {
  depth: number;
  hint: string;
  selectedComponent: OptionComponent | undefined;
};

const getChipDisplayState = ({
  component,
  isLastGroup,
}: {
  component: OptionComponent;
  isLastGroup: boolean;
}) => {
  const isSoldout = component.goods_option?.is_soldout ?? false;
  const deliveryType = component.delivery_type;
  const isShakOrToday = deliveryType === 'shak' || deliveryType === 'today';
  const isThirdPL = deliveryType === 'third_pl';
  const stock = component.goods_option?.stock ?? -1;
  const showLowStock =
    !isSoldout &&
    isLastGroup &&
    deliveryType !== 'shak' &&
    stock >= 1 &&
    stock <= 9;
  const propertyText = isSoldout
    ? '품절'
    : isShakOrToday || isThirdPL
      ? ''
      : '일반배송';
  const stockText = showLowStock ? `${stock}개 남음` : '';
  const showDeliveryBadge = (isShakOrToday || isThirdPL) && !isSoldout;
  const additionalPrice = component.option_minimum_additional_price;
  const showAdditionalPrice = !isSoldout && additionalPrice !== 0;
  const additionalPriceText = showAdditionalPrice
    ? `${additionalPrice > 0 ? '+' : ''}${localizeNumber(additionalPrice)}원`
    : '';

  return {
    isSoldout,
    isThirdPL,
    showDeliveryBadge,
    showLowStock,
    propertyText,
    stockText,
    showAdditionalPrice,
    additionalPriceText,
  };
};

const InlineOptionChip = ({
  component,
  isSelected,
  isFirstGroup,
  isLastGroup,
  candidatesCount,
  onPress,
  onLayout,
  onTitleLayout,
  cardHeight,
}: {
  component: OptionComponent;
  isSelected: boolean;
  isFirstGroup: boolean;
  isLastGroup: boolean;
  candidatesCount: number;
  onPress: () => void;
  onLayout?: (event: LayoutChangeEvent) => void;
  onTitleLayout: (lineCount: number) => void;
  cardHeight: number;
}) => {
  const {
    isSoldout,
    isThirdPL,
    showDeliveryBadge,
    showLowStock,
    propertyText,
    stockText,
    showAdditionalPrice,
    additionalPriceText,
  } = getChipDisplayState({ component, isLastGroup });
  const showHighDemandBadge =
    isFirstGroup &&
    candidatesCount > 1 &&
    !isSoldout &&
    component.has_high_demand_tag;

  const backgroundColor = isSoldout
    ? 'bg_secondary'
    : isSelected
      ? 'bg_lilac_primary'
      : 'bg_always_white';
  const borderColor = isSelected
    ? 'content_blue_primary'
    : isSoldout
      ? 'border_secondary'
      : 'border_primary';
  const textColor =
    isSoldout && !isSelected ? 'content_secondary' : 'content_primary';
  const propertyColor = isSoldout ? 'content_tertiary' : 'content_secondary';
  const nameType = isSoldout ? 'body2' : 'subtitle2';

  return (
    <Flex padding={{ top: 12 }} onLayout={onLayout}>
      <Flex
        minWidth={CHIP_MIN_WIDTH}
        maxWidth={CHIP_MAX_WIDTH}
        height={cardHeight}
        borderRadius={4}
        borderWidth={1}
        borderColor={borderColor}
        backgroundColor={backgroundColor}
        padding={{
          horizontal: 8,
          top: 12,
          bottom: showLowStock ? 8 : 12,
        }}
        alignItems={'center'}
        justifyContent={'center'}
        gap={4}
        onPress={onPress}
      >
        <Typography
          type={nameType}
          color={textColor}
          numberOfLines={2}
          textAlign={'center'}
          onTextLayout={event => {
            onTitleLayout(event.nativeEvent.lines.length);
          }}
        >
          {component.name}
        </Typography>
        <Flex gap={2} alignItems={'center'}>
          {showDeliveryBadge && (
            <Flex
              width={54}
              height={CHIP_PROPERTY_HEIGHT}
              alignItems={'center'}
              justifyContent={'center'}
            >
              {isThirdPL ? (
                <IconBrandIconThirdPL width={49} height={13} />
              ) : (
                <InlineTodayDeliveryIcon />
              )}
            </Flex>
          )}
          {propertyText.length > 0 && (
            <Flex
              height={CHIP_PROPERTY_HEIGHT}
              alignItems={'center'}
              justifyContent={'center'}
            >
              <Typography
                type={'body5'}
                color={propertyColor}
                textAlign={'center'}
                numberOfLines={1}
              >
                {propertyText}
              </Typography>
            </Flex>
          )}
          {showAdditionalPrice && (
            <Typography
              type={'body5'}
              color={'content_tertiary'}
              textAlign={'center'}
              numberOfLines={1}
            >
              {additionalPriceText}
            </Typography>
          )}
        </Flex>
        {showLowStock && (
          <Flex
            gap={8}
            alignItems={'center'}
            alignSelf={'stretch'}
            padding={{ top: 4 }}
          >
            <Flex
              alignSelf={'stretch'}
              borderTopWidth={1}
              borderColor={'border_tertiary'}
            />
            <Typography
              type={'body5'}
              color={'content_pink_primary'}
              textAlign={'center'}
              numberOfLines={1}
            >
              {stockText}
            </Typography>
          </Flex>
        )}
      </Flex>
      {showHighDemandBadge && (
        <Flex position={'absolute'} top={3} right={-5}>
          <Flex
            borderWidth={1}
            borderColor={'pink10'}
            backgroundColor={'pink5'}
            borderRadius={999}
            padding={{ horizontal: 6, vertical: 2 }}
          >
            <Typography type={'subtitle6'} color={'content_pink_primary'}>
              주문많음
            </Typography>
          </Flex>
        </Flex>
      )}
    </Flex>
  );
};

const InlineOptionGroupHeader = ({
  hint,
  selectedValue,
}: {
  hint: string;
  selectedValue: string | undefined;
}) => {
  return (
    <Flex.Row gap={8} padding={{ horizontal: 16 }} alignItems={'flex-start'}>
      <Typography type={'body2'} color={'content_secondary'}>
        {hint}
      </Typography>
      {selectedValue && (
        <Flex flex={1}>
          <Typography
            type={'subtitle2'}
            color={'content_primary'}
            numberOfLines={1}
          >
            {selectedValue}
          </Typography>
        </Flex>
      )}
    </Flex.Row>
  );
};

const InlineOptionGroup = ({
  group,
  candidates,
  isFirstGroup,
  isLastGroup,
  onSelect,
}: {
  group: InlineOptionGroupView;
  candidates: OptionComponent[];
  isFirstGroup: boolean;
  isLastGroup: boolean;
  onSelect: (component: OptionComponent) => void;
}) => {
  const selectedSno = group.selectedComponent?.goods_option_sno;
  const candidatesCount = candidates.length;
  const { width: viewportWidth } = useScreenSize();

  const scrollViewRef = useRef<ScrollView>(null);
  const chipLayoutsRef = useRef<Map<number, { x: number; width: number }>>(
    new Map(),
  );
  const titleLineCountsRef = useRef<Map<number, number>>(new Map());
  const [maxTitleLineCount, setMaxTitleLineCount] = useState(1);
  const { hasAdditionalPrice, hasStockText } = useMemo(() => {
    const states = candidates.map(component =>
      getChipDisplayState({ component, isLastGroup }),
    );
    return {
      hasAdditionalPrice: states.some(state => state.showAdditionalPrice),
      hasStockText: states.some(state => state.showLowStock),
    };
  }, [candidates, isLastGroup]);
  const cardHeight =
    CHIP_BASE_HEIGHT +
    (maxTitleLineCount > 1 ? CHIP_LINE_HEIGHT : 0) +
    (hasAdditionalPrice ? CHIP_LINE_HEIGHT : 0) +
    (hasStockText ? CHIP_STOCK_BLOCK_HEIGHT : 0);

  useEffect(() => {
    titleLineCountsRef.current.clear();
    setMaxTitleLineCount(1);
  }, [candidates]);

  useEffect(() => {
    if (isNil(selectedSno) || viewportWidth === 0) return;
    const layout = chipLayoutsRef.current.get(selectedSno);
    if (!layout) return;
    const targetX = Math.max(
      0,
      layout.x + layout.width / 2 - viewportWidth / 2,
    );
    scrollViewRef.current?.scrollTo({ x: targetX, animated: true });
  }, [selectedSno, viewportWidth, candidates]);

  return (
    <Flex>
      <InlineOptionGroupHeader
        hint={group.hint}
        selectedValue={group.selectedComponent?.name}
      />
      <ScrollView
        ref={scrollViewRef}
        horizontal={true}
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={{ paddingHorizontal: 16, gap: 8 }}
      >
        {candidates.map(component => (
          <InlineOptionChip
            key={component.sno}
            component={component}
            isSelected={component.goods_option_sno === selectedSno}
            isFirstGroup={isFirstGroup}
            isLastGroup={isLastGroup}
            candidatesCount={candidatesCount}
            onPress={() => onSelect(component)}
            onLayout={event => {
              const { x, width } = event.nativeEvent.layout;
              chipLayoutsRef.current.set(component.goods_option_sno, {
                x,
                width,
              });
            }}
            onTitleLayout={lineCount => {
              titleLineCountsRef.current.set(
                component.goods_option_sno,
                lineCount,
              );
              const nextMaxLineCount = Math.max(
                1,
                ...titleLineCountsRef.current.values(),
              );
              setMaxTitleLineCount(prev =>
                prev === nextMaxLineCount ? prev : nextMaxLineCount,
              );
            }}
            cardHeight={cardHeight}
          />
        ))}
      </ScrollView>
    </Flex>
  );
};

export const InlineOptionGroupList = ({
  groups,
  candidatesByDepth,
  onSelect,
}: {
  groups: InlineOptionGroupView[];
  candidatesByDepth: { [depth: number]: OptionComponent[] };
  onSelect: (component: OptionComponent) => void;
}) => {
  const lastDepth = groups.reduce(
    (max, group) => (group.depth > max ? group.depth : max),
    0,
  );
  return (
    <Flex padding={{ bottom: 20 }} gap={20}>
      {groups.map(group => {
        const candidates = candidatesByDepth[group.depth] ?? [];
        if (candidates.length === 0) return null;
        return (
          <InlineOptionGroup
            key={group.depth}
            group={group}
            candidates={candidates}
            isFirstGroup={group.depth === 1}
            isLastGroup={group.depth === lastDepth}
            onSelect={onSelect}
          />
        );
      })}
    </Flex>
  );
};

export const InlineSelectedAttributeList = ({
  groups,
}: {
  groups: InlineOptionGroupView[];
}) => {
  return (
    <Flex padding={{ horizontal: 16 }}>
      {groups.map((group, index) => {
        if (!group.selectedComponent) return null;
        return (
          <Flex.Row
            key={group.depth}
            alignItems={'center'}
            gap={12}
            padding={{ top: index === 0 ? 12 : 8 }}
          >
            <Typography type={'body2'} color={'content_secondary'}>
              {group.hint}
            </Typography>
            <Flex flex={1}>
              <Typography
                type={'subtitle2'}
                color={'content_primary'}
                numberOfLines={1}
              >
                {group.selectedComponent.name}
              </Typography>
            </Flex>
          </Flex.Row>
        );
      })}
    </Flex>
  );
};
